import type { JSX } from 'react';
import type { WireArtifact } from '@logistics/contracts';
import { amountIn } from '@/lib/format';
import { Amount, FactLines, StatusPill, Truncated, caption } from './parts';

type Money = Extract<WireArtifact, { kind: 'money' }>;
type Line = Money['lines'][number];

/**
 * What the loads are worth. The amounts are the numbers the records carried —
 * no symbol is drawn unless the provider named a currency, and nothing is
 * converted; the total is the server's own sum.
 */
export function MoneyArtifact({ artifact }: { artifact: Money }): JSX.Element {
  const currency = artifact.currency;
  return (
    <div className="flex flex-col gap-2.5">
      {artifact.total !== undefined && artifact.lines.length > 1 && (
        <div className="border-line bg-surf2 flex items-baseline justify-between gap-3 rounded-xl border px-4 py-3">
          <span className={caption}>TOTAL</span>
          <span className="font-mono text-lg font-medium tabular-nums">
            {amountIn(artifact.total, currency)}
          </span>
        </div>
      )}
      {artifact.lines.map((line, i) => (
        <MoneyLine key={i} line={line} currency={currency} />
      ))}
      {artifact.lines.length === 0 && (
        <p className="text-mute text-[13px]">No amounts on these loads.</p>
      )}
      {artifact.truncated && <Truncated what={`${artifact.lines.length} loads`} />}
    </div>
  );
}

function MoneyLine({ line, currency }: { line: Line; currency: string | undefined }): JSX.Element {
  const amounts = [
    { label: 'Rate', value: line.haulingRate },
    { label: 'Accessorials', value: line.accessorials },
    { label: 'Total', value: line.total },
  ].filter((a): a is { label: string; value: number } => a.value !== undefined);
  return (
    <div className="border-line bg-surf rounded-xl border px-4 py-3.5">
      <div className="flex items-center justify-between gap-2.5">
        <span className="min-w-0 truncate font-mono text-sm font-medium">
          #{line.reference.replace(/^#/, '')}
        </span>
        {line.status !== undefined && <StatusPill status={line.status} />}
      </div>
      {line.customer !== undefined && (
        <div className="text-mute mt-1 truncate text-[12.5px]">{line.customer}</div>
      )}
      {amounts.length > 0 && (
        <div className="mt-2.5 grid grid-cols-3 gap-2.5">
          {amounts.map((a) => (
            <Amount key={a.label} label={a.label} value={amountIn(a.value, currency)} />
          ))}
        </div>
      )}
      <FactLines
        facts={[
          ...(line.remitTo === undefined ? [] : [{ k: 'Remit to', v: line.remitTo }]),
          ...line.facts,
        ]}
      />
    </div>
  );
}
